/**
 * <announcement-bar-custom>
 * Barre d'annonce autonome et personnalisable.
 * Aucune dépendance externe (pas de Flickity) afin de cohabiter sans conflit
 * avec la barre d'annonce du header (#AnnouncementSlider).
 *
 * Data-attributes pilotés par le schéma de la section :
 *  - data-animation       : "none" | "fade" | "slide" | "marquee"
 *  - data-interval        : intervalle de rotation en ms
 *  - data-marquee-speed   : vitesse marquee (px/s)
 *  - data-pause-on-hover  : "true" | "false"
 *  - data-dismissible     : "true" | "false"
 *  - data-section-id      : id de la section (clé localStorage)
 */
(function () {
  if (window.customElements && window.customElements.get('announcement-bar-custom')) return;

  var STORAGE_PREFIX = 'abc-dismissed-';

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class AnnouncementBarCustom extends HTMLElement {
    connectedCallback() {
      this.animation = this.dataset.animation || 'none';
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.marqueeSpeed = parseInt(this.dataset.marqueeSpeed, 10) || 50;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.dismissible = this.dataset.dismissible === 'true';
      this.sectionId = this.dataset.sectionId || '';

      this.track = this.querySelector('.abc__track');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.abc__slide'));
      // Les bandeaux sont rendus par content_for : on numérote au runtime.
      this.slides.forEach(function (sl, i) {
        sl.dataset.index = i;
      });

      if (this.dismissible && this.isDismissed()) {
        this.hide();
        return;
      }

      this.setupDismiss();
      this.initStackHeight();

      // Initialise tout widget Trustpilot présent, quel que soit le mode d'animation.
      if (this.querySelector('.trustpilot-widget')) this.observeTrustpilot();

      if (this.slides.length <= 1) {
        if (this.slides[0]) this.slides[0].classList.add('is-active');
        return;
      }

      if (this.animation === 'marquee' && !reduceMotion()) {
        this.initMarquee();
      } else if ((this.animation === 'fade' || this.animation === 'slide') && !reduceMotion()) {
        this.initRotation();
      } else {
        // Aucune animation : on empile, on n'affiche que la première.
        this.slides[0].classList.add('is-active');
      }
    }

    disconnectedCallback() {
      this.stopRotation();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onRotationResize) window.removeEventListener('resize', this._onRotationResize);
      if (this._tpObserver) this._tpObserver.disconnect();
      if (this._stackRO) this._stackRO.disconnect();
      if (this._stackRAF) window.cancelAnimationFrame(this._stackRAF);
      // Empêche qu'un timer Trustpilot en attente replanifie un rAF après teardown.
      this._stackScheduled = false;
      if (this._tpTimers) {
        this._tpTimers.forEach(function (t) {
          window.clearTimeout(t);
        });
        this._tpTimers = [];
      }
    }

    /* ----- Mode ligne fixe : initialise + re-mesure le(s) widget(s) Trustpilot ----- */
    observeTrustpilot() {
      var widgets = Array.prototype.slice.call(this.querySelectorAll('.trustpilot-widget'));
      if (!widgets.length) return;
      var self = this;
      this._tpTimers = this._tpTimers || [];

      // Le bootstrap Trustpilot ne scanne les widgets qu'à son chargement. Un widget
      // rendu dynamiquement (ou présent alors que le script tourne déjà pour le bandeau
      // hardcodé) doit être initialisé explicitement via Trustpilot.loadFromElement.
      var tries = 0;
      (function loadWidgets() {
        if (window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function') {
          widgets.forEach(function (w) {
            try {
              window.Trustpilot.loadFromElement(w, true);
            } catch (e) {}
          });
          self._scheduleStackHeight();
          return;
        }
        // Le script est async : on réessaie jusqu'à ~10 s.
        if (tries++ < 100) self._tpTimers.push(window.setTimeout(loadWidgets, 100));
      })();

      // Re-mesure de la hauteur quand le widget injecte son contenu (il grandit après coup).
      if (window.MutationObserver) {
        var mo = new MutationObserver(function () {
          self._scheduleStackHeight();
        });
        widgets.forEach(function (w) {
          mo.observe(w, { childList: true, subtree: true, attributes: true });
        });
        this._tpObserver = mo;
        // On arrête d'observer une fois le widget stabilisé.
        this._tpTimers.push(
          setTimeout(function () {
            mo.disconnect();
          }, 10000),
        );
      }
      // Filets de sécurité temporels (le script tiers peut être lent).
      [400, 1200, 3000, 6000].forEach(function (d) {
        self._tpTimers.push(
          setTimeout(function () {
            self._scheduleStackHeight();
          }, d),
        );
      });
    }

    /* ----- Pile sticky : publie la hauteur du bandeau pour décaler le header ----- */
    initStackHeight() {
      var self = this;
      // Init SYNCHRONE : pilote le layout du 1er paint du header sticky, ne pas différer.
      this.updateStackHeight();
      // Re-mesures ultérieures : batchées via rAF débouncé (évite les reflows synchrones).
      this._scheduleStackHeight();
      window.addEventListener('load', function () {
        self._scheduleStackHeight();
      });
      this._onResize = function () {
        self._scheduleStackHeight();
      };
      window.addEventListener('resize', this._onResize);
      // Re-mesure automatique dès que la taille réelle du bandeau change : contenu
      // masqué (ex. bloc Trustpilot), reflow responsive, widget tiers qui se charge ou
      // disparaît. Le bandeau étant position:fixed, modifier --abc-stack-h n'affecte
      // pas sa propre taille => pas de boucle ResizeObserver.
      if (window.ResizeObserver) {
        this._stackRO = new ResizeObserver(function () {
          self._scheduleStackHeight();
        });
        this._stackRO.observe(this);
      }
    }

    /* Ordonnanceur rAF débouncé : coalesce les déclencheurs async/répétés
       (resize, ResizeObserver, MutationObserver, setTimeout, load) en une seule
       mesure+écriture par frame. Le drapeau _stackScheduled empêche la réentrance. */
    _scheduleStackHeight() {
      if (this._stackScheduled) return;
      this._stackScheduled = true;
      var self = this;
      this._stackRAF = window.requestAnimationFrame(function () {
        self._stackScheduled = false;
        self.updateStackHeight();
      });
    }

    updateStackHeight() {
      // Le bandeau ne contribue à la pile QUE s'il est réellement visible.
      // Masqué (dismiss, ou media query show_on_mobile / show_on_desktop => display:none),
      // sa hauteur réservée doit retomber à 0, sinon un espace vide subsiste en haut.
      // --- Phase LECTURE : toutes les mesures géométriques d'abord ---
      var hidden = this.hasAttribute('hidden') || window.getComputedStyle(this).display === 'none';
      var h = 0;
      if (hidden === false) {
        h = this.offsetHeight;
      }
      // --- Phase ÉCRITURE : aucune lecture après ce point ---
      document.documentElement.style.setProperty('--abc-stack-h', h + 'px');
    }

    /* ----- Dismiss ----- */
    storageKey() {
      return STORAGE_PREFIX + this.sectionId;
    }

    isDismissed() {
      try {
        return window.localStorage.getItem(this.storageKey()) === '1';
      } catch (e) {
        return false;
      }
    }

    setupDismiss() {
      var btn = this.querySelector('.abc__close');
      if (!btn) return;
      var self = this;
      btn.addEventListener('click', function () {
        try {
          window.localStorage.setItem(self.storageKey(), '1');
        } catch (e) {}
        self.hide();
      });
    }

    hide() {
      this.style.display = 'none';
      this.setAttribute('hidden', '');
      // Le bandeau ne réserve plus d'espace : le header remonte tout en haut.
      document.documentElement.style.setProperty('--abc-stack-h', '0px');
    }

    /* Un bandeau peut être masqué au breakpoint courant (réglage par bandeau
       show_on_desktop / show_on_mobile => display:none via media query). */
    isHidden(slide) {
      return !slide || window.getComputedStyle(slide).display === 'none';
    }

    /* ----- Rotation (fade / slide) ----- */
    initRotation() {
      var self = this;
      this.index = 0;
      // Démarre sur le premier bandeau réellement visible (saute les masqués).
      this.goTo(0);

      this.dots = Array.prototype.slice.call(this.querySelectorAll('.abc__dot'));
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          self.goTo(i);
          self.restartRotation();
        });
      });

      var prev = this.querySelector('.abc__arrow--prev');
      var next = this.querySelector('.abc__arrow--next');
      if (prev)
        prev.addEventListener('click', function () {
          self.goTo(self.index - 1);
          self.restartRotation();
        });
      if (next)
        next.addEventListener('click', function () {
          self.goTo(self.index + 1);
          self.restartRotation();
        });

      var pause = this.querySelector('.abc__pause');
      if (pause) {
        pause.addEventListener('click', function () {
          if (self.timer) {
            self.stopRotation();
            pause.classList.add('is-paused');
          } else {
            self.startRotation();
            pause.classList.remove('is-paused');
          }
        });
      }

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', function () {
          self.stopRotation();
        });
        this.addEventListener('mouseleave', function () {
          self.startRotation();
        });
      }

      // Au changement de breakpoint, si le bandeau actif vient d'être masqué,
      // on rebascule vers le suivant visible (sans effet s'il reste visible).
      this._onRotationResize = function () {
        self.goTo(self.index);
      };
      window.addEventListener('resize', this._onRotationResize);

      this.startRotation();
    }

    goTo(i) {
      var count = this.slides.length;
      i = ((i % count) + count) % count;
      // Saute les bandeaux masqués au breakpoint courant ; garde anti-boucle
      // pour le cas limite où tous les bandeaux seraient masqués.
      var guard = 0;
      while (this.isHidden(this.slides[i]) && guard < count) {
        i = (i + 1) % count;
        guard++;
      }
      this.index = i;
      var self = this;
      this.slides.forEach(function (s, idx) {
        s.classList.toggle('is-active', idx === self.index);
      });
      if (this.dots) {
        this.dots.forEach(function (dot, idx) {
          dot.classList.toggle('is-active', idx === self.index);
        });
      }
    }

    startRotation() {
      if (this.timer) return;
      var self = this;
      this.timer = window.setInterval(function () {
        self.goTo(self.index + 1);
      }, this.interval);
    }

    stopRotation() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartRotation() {
      this.stopRotation();
      this.startRotation();
    }

    /* ----- Marquee ----- */
    initMarquee() {
      if (!this.track) return;
      this.classList.add('abc--marquee');

      // Duplique le contenu pour un défilement continu sans rupture.
      var original = this.track.innerHTML;
      this.track.innerHTML = original + original;
      this.slides.forEach(function (s) {
        s.classList.add('is-active');
      });

      var self = this;
      var applyDuration = function () {
        // largeur d'une copie (la moitié du track)
        var width = self.track.scrollWidth / 2;
        var duration = width / self.marqueeSpeed; // s
        self.track.style.setProperty('--abc-marquee-duration', duration + 's');
      };
      applyDuration();

      if (window.ResizeObserver) {
        this._ro = new ResizeObserver(applyDuration);
        this._ro.observe(this);
      } else {
        window.addEventListener('resize', applyDuration);
      }

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', function () {
          self.track.style.animationPlayState = 'paused';
        });
        this.addEventListener('mouseleave', function () {
          self.track.style.animationPlayState = 'running';
        });
      }
    }
  }

  window.customElements.define('announcement-bar-custom', AnnouncementBarCustom);
})();
